import { ReactNode } from 'react'
import { TableCell, TableSortLabel, Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

export type SortOrder = 'asc' | 'desc'

// Inlined from @mui/utils' visuallyHidden to keep this leaf component free of
// extra dependencies.
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  whiteSpace: 'nowrap' as const,
  width: '1px',
}

interface SortableTableHeaderProps {
  /** Column identifier. `sortKey` is accepted as an alias for parity with CrowCase. */
  id?: string
  sortKey?: string
  label: ReactNode
  orderBy?: string
  order?: SortOrder
  onSort?: (columnId: string) => void
  align?: 'left' | 'right' | 'center' | 'justify' | 'inherit'
  sx?: SxProps<Theme>
}

/**
 * Reusable sortable table-header cell — ported from CrowCase
 * (common/table/SortableTableHeader.jsx). Renders a MUI TableSortLabel that
 * reflects the current sort column/direction and calls onSort(columnId).
 */
export default function SortableTableHeader({
  id,
  sortKey,
  label,
  orderBy,
  order = 'asc',
  onSort,
  align = 'left',
  sx,
}: SortableTableHeaderProps) {
  const columnId = id || sortKey || ''
  const isSorted = orderBy === columnId

  return (
    <TableCell
      align={align}
      sortDirection={isSorted ? order : false}
      sx={[{ fontWeight: 600 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <TableSortLabel
        active={isSorted}
        direction={isSorted ? order : 'asc'}
        onClick={() => onSort?.(columnId)}
      >
        {label}
        {isSorted ? (
          <Box component="span" sx={visuallyHidden}>
            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </Box>
        ) : null}
      </TableSortLabel>
    </TableCell>
  )
}
